import { useEffect, useMemo, useRef, useState, Component } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import Plot from "react-plotly.js";
import mermaid from "mermaid";
import dayjs from "dayjs";
import "./Index.css";
import { BASE_URL } from "../../configs";
import { getAccessToken } from "../Protected";
import Loader from "../../components/recharts/loader.gif";
import { SendHorizontal, Trash2 } from "lucide-react";
import { normalizeDisplayLabel } from "../../utility/displayText";
const UI_FONT = "Space Grotesk, sans-serif";
const BRAND_COLOR = "#14b8a6";
const ACCENT_COLOR = "#f59e0b";

/* Theme Constants */
const THEME = {
  userBubble: "user-bubble",
  assistantCard: "assistant-card",
  errorCard: "assistant-card error-state",
};

/* Error Boundary to catch crashes */
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: "20px",
            color: "#ef4444",
            background: "#fee2e2",
            borderRadius: "8px",
            margin: "20px",
            border: "1px solid #fca5a5",
          }}
        >
          <h3>Something went wrong.</h3>
          <p style={{ fontSize: "12px", fontFamily: "monospace" }}>
            {this.state.error?.message}
          </p>
          <button
            className="btn-primary"
            onClick={() => window.location.reload()}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const fallbackPrompts = [
  "What was the highest MCP yesterday?",
  "Compare DAM vs RTM volumes this week",
  "Show me hourly MCP trend for the last 7 days",
  "List exchanges ranked by average volume this month",
];

/* Helper: Check if column should be hidden (ID columns) */
const isIdColumn = (name: any) => {
  if (!name || typeof name !== "string") return false;
  const lowerName = name.toLowerCase().trim();
  const idPatterns = [
    "_id",
    "entity_id",
    "product_id",
    "exchange_id",
    "type_id",
    "typee_id",
    "slot_id",
    "role_rank",
  ];
  return idPatterns.some(
    (pattern) => lowerName.includes(pattern) || lowerName === pattern,
  );
};

/* Helper: Format snake_case column names to readable Title Case */
const formatColumnName = (name: string) => {
  if (!name || typeof name !== "string") return name;
  const columnNameMap: Record<string, string> = {
    date: "Date",
    role_type: "Role",
    entity_name: "Entity",
    drawal_entity_name: "Entity",
    exchange_name: "Exchange",
    product_name: "Product",
    total_volume_mwh: "Volume (MWh)",
    total_traded_mwh: "Traded (MWh)",
    volume_mwh: "Volume (MWh)",
    vwap_rs_kwh: "VWAP (₹/kWh)",
    mcp_rs_kwh: "MCP (₹/kWh)",
    avg_price_rs_kwh: "Avg Price (₹/kWh)",
    max_mcp_rs_kwh: "Max MCP (₹/kWh)",
    dam_price_rs_kwh: "DAM Price (₹/kWh)",
    rtm_price_rs_kwh: "RTM Price (₹/kWh)",
    vw_mcp_rs_mwh: "VWAP (₹/MWh)",
    vwap_mcp_rs_mwh: "VWAP (₹/MWh)",
    mcp_rs_mwh: "MCP (₹/MWh)",
    max_mcp_rs_mwh: "Max MCP (₹/MWh)",
    avg_rtm_price_rs_mwh: "Avg RTM (₹/MWh)",
    dam_mcp_rs_mwh_vwa: "DAM Price (₹/MWh)",
    mcv_mw: "MCV (MW)",
    total_mcv_mw: "Total MCV (MW)",
    month: "Month",
  };
  const lowerName = name.toLowerCase().trim();
  if (columnNameMap[lowerName]) return columnNameMap[lowerName];
  return normalizeDisplayLabel(name).replace(/\b\w/g, (c) => c.toUpperCase());
};

/* --- MERMAID COMPONENT --- */
mermaid.initialize({
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
  fontFamily: UI_FONT,
});

const Mermaid = ({ chart }: { chart: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState("");

  useEffect(() => {
    const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
    if (chart) {
      const normalizeMermaid = (input: string) => {
        const cleaned = String(input || "").replace(/•/g, "- ");
        const lines = cleaned.split("\n").map((line) => {
          const trimmed = line.trim();
          if (!trimmed) return "";
          if (trimmed.startsWith("%%")) return line;
          if (/^(flowchart|graph)\b/i.test(trimmed)) return line;
          if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
            return `%% ${trimmed.replace(/^[-*]\s+/, "")}`;
          }
          const hasArrow = /(-->|==>)/.test(trimmed);
          if (
            hasArrow &&
            !/^[A-Za-z0-9_]+\s*[\[\(\{]/.test(trimmed) &&
            !/^[A-Za-z0-9_]+\s*(-->|==>)/.test(trimmed)
          ) {
            const m = trimmed.match(/[A-Za-z0-9_]+\s*(-->|==>)/);
            if (m && typeof m.index === "number") {
              return trimmed.slice(m.index);
            }
          }
          if (!hasArrow && !/^[A-Za-z0-9_]+\s*[\[\(\{]/.test(trimmed)) {
            return `%% ${trimmed}`;
          }
          return line;
        });
        return lines.join("\n");
      };

      const safeChart = normalizeMermaid(chart);
      mermaid
        .render(id, safeChart)
        .then(({ svg }) => {
          setSvg(svg);
        })
        .catch((e) => {
          console.error("Mermaid error:", e);
          setSvg(
            `<div class="error" style="color:red; padding:10px; border:1px solid red;">Failed to render diagram: ${e.message}</div>` +
              `<pre style="margin-top:8px; white-space:pre-wrap;">${safeChart.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</pre>`,
          );
        });
    }
  }, [chart]);

  return (
    <div
      className="mermaid-diagram"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

const markdownComponents: any = {
  table: ({ children }: any) => (
    <div className="data-table-card">
      <div className="table-header-context">
        <span>Data View</span>
      </div>
      <div className="table-scroller">
        <table className="dense-table">{children}</table>
      </div>
    </div>
  ),
  th: ({ children }: any) => {
    const headerText = Array.isArray(children) ? children.join("") : children;
    return (
      <th className="numeric-chk">{formatColumnName(String(headerText))}</th>
    );
  },
  td: ({ children }: any) => {
    const val = Array.isArray(children) ? children[0] : children;
    const isNum = typeof val === "string" && /^[\d,.]+$/.test(val.trim());
    return <td className={isNum ? "numeric" : ""}>{children}</td>;
  },
  code({ inline, className, children }: any) {
    const match = /language-(\w+)/.exec(className || "");
    if (!inline && match && match[1] === "mermaid") {
      return <Mermaid chart={String(children).replace(/\n$/, "")} />;
    }
    return !inline ? (
      <pre>
        <code>{children}</code>
      </pre>
    ) : (
      <code>{children}</code>
    );
  },
};

/* --- COMPONENT: TABBED RESULT RENDERER --- */
interface TabSwitcherProps {
  tabs: string[];
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const TabSwitcher: React.FC<TabSwitcherProps> = ({
  tabs,
  activeTab,
  onTabChange,
}) => (
  <div className="tab-header">
    {tabs.map((tab) => (
      <button
        key={tab}
        className={`tab-btn ${activeTab === tab ? "active" : ""}`}
        onClick={() => onTabChange(tab)}
      >
        {tab}
      </button>
    ))}
  </div>
);

interface SimpleTableProps {
  csv: string;
  onDownload?: () => void;
}

const SimpleTableFromCsv: React.FC<SimpleTableProps> = ({
  csv,
  onDownload,
}) => {
  if (!csv) return <div style={{ color: "white" }}>No data available</div>;
  const rows = csv
    .trim()
    .split("\n")
    .map((r) => r.split(","));
  const header = rows[0];
  const body = rows.slice(1);

  return (
    <div className="data-table-card">
      <div className="table-header-context">
        <span>Data View</span>
        {onDownload && (
          <button className="download-csv-btn" onClick={onDownload}>
            <span style={{ fontSize: "14px" }}>⬇</span> Download CSV
          </button>
        )}
      </div>
      <div className="table-scroller">
        <table className="dense-table">
          <thead>
            <tr>
              {header.map((h, i) => (
                <th key={i} className="numeric-chk">
                  {formatColumnName(h)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => {
                  const isNum = /^[\d,.]+$/.test(cell.trim());
                  return (
                    <td key={j} className={isNum ? "numeric" : ""}>
                      {cell}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface MessageData {
  id: string;
  role: "user" | "assistant";
  content: string;
  sql?: string;
  question?: string;
  csv?: string;
  plot_b64?: string;
  plot_json?: string;
  chart_data?: string;
  meta?: any;
  ts: number;
  isError?: boolean;
  isStreaming?: boolean;
}

interface ResultRendererProps {
  message: MessageData;
  onDownloadCsv?: (msg: MessageData) => void;
  onSend?: (text: string, options?: any) => void;
  onCopy?: (text: string) => void;
  onFeedback?: (type: "up" | "down", msg: MessageData) => void;
  feedback?: Record<string, "up" | "down">;
  metaOptions?: any;
}

const ResultRenderer: React.FC<ResultRendererProps> = ({
  message,
  onDownloadCsv,
  onSend,
  onCopy,
  onFeedback,
  feedback,
  metaOptions,
}) => {
  const { meta, plot_json, plot_b64, csv, chart_data } = message;
  const isChartable = meta?.is_chartable;
  const showTable = meta?.show_table !== false;
  const footerRuntime =
    typeof meta?.runtime_s === "number" ? `${meta.runtime_s.toFixed(2)}s` : "";
  const feedbackValue = feedback?.[message.id];

  const [activeX, setActiveX] = useState(meta?.chart_meta?.default_x || "");
  const [activeY, setActiveY] = useState(meta?.chart_meta?.default_y || "");

  useEffect(() => {
    if (meta?.chart_meta) {
      setActiveX(meta.chart_meta.default_x || "");
      setActiveY(meta.chart_meta.default_y || "");
    }
  }, [message.id, meta?.chart_meta]);

  const tabs = ["Summary"];
  if (isChartable) {
    tabs.push("Chart");
    if (showTable) tabs.push("Table");
  }

  const [activeTab, setActiveTab] = useState("Summary");

  const [selectedEntity, setSelectedEntity] = useState(
    meta?.identified_entity || "",
  );
  const [selectedExchanges, setSelectedExchanges] = useState(
    meta?.dimensions?.defaults?.exchange
      ? [meta.dimensions.defaults.exchange]
      : ["IEX"],
  );
  const [selectedProducts, setSelectedProducts] = useState(
    meta?.dimensions?.defaults?.product
      ? [meta.dimensions.defaults.product]
      : ["DAM"],
  );
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("all");
  const [selectedVolumeCategory, setSelectedVolumeCategory] = useState("");

  const [selectedYear, setSelectedYear] = useState("none");
  const [periodMode, setPeriodMode] = useState("month");
  const [periodValue, setPeriodValue] = useState({
    month: dayjs().format("MMM"),
    year: dayjs().format("YYYY"),
    startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  });

  const slotOptions = [
    { value: "all", label: "All slots" },
    { value: "solar hours", label: "Solar hours" },
    { value: "morning peak", label: "Morning peak" },
    { value: "evening peak", label: "Evening peak" },
    { value: "peak hours", label: "Peak hours" },
    { value: "non peak hours", label: "Non-peak hours" },
    { value: "night hours", label: "Night hours" },
  ];

  const yearOptions = [
    { value: "none", label: "Year: auto" },
    { value: "last3", label: "Last 3 years" },
  ];

  useEffect(() => {
    if (meta) {
      if (meta.identified_entity) setSelectedEntity(meta.identified_entity);

      if (meta.dimensions?.defaults?.exchange) {
        setSelectedExchanges([meta.dimensions.defaults.exchange]);
      }
      if (meta.dimensions?.defaults?.product) {
        setSelectedProducts([meta.dimensions.defaults.product]);
      }
      setSelectedRegion("");
      setSelectedSlot("all");
      setSelectedVolumeCategory("");

      setSelectedYear("none");
      if (meta.dimensions?.period_suggestion) {
        setPeriodMode("month");
      }
    }
  }, [message.id, meta]);

  const toggleSelection = (
    list: string[],
    setList: (list: string[]) => void,
    item: string,
  ) => {
    if (list.includes(item)) {
      setList(list.filter((i) => i !== item));
    } else {
      setList([...list, item]);
    }
  };

  const handleApply = () => {
    let finalQuery = meta?.initial_query || message.content;
    const originalToken = meta?.original_token;
    const regionList = Array.isArray(metaOptions?.regions)
      ? metaOptions.regions
      : [];

    if (originalToken && selectedEntity) {
      const escapedToken = originalToken.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(`\\b${escapedToken}\\b`, "gi");
      if (re.test(finalQuery)) {
        finalQuery = finalQuery.replace(re, selectedEntity);
      } else {
        finalQuery = selectedEntity + " " + finalQuery;
      }
    } else if (selectedEntity) {
      finalQuery = selectedEntity + " " + finalQuery;
    }

    const queryLower = finalQuery.toLowerCase();

    const exchangeStr = selectedExchanges.join(", ");
    if (
      exchangeStr &&
      !selectedExchanges.some((ex) => queryLower.includes(ex.toLowerCase()))
    ) {
      finalQuery = finalQuery + " on " + exchangeStr;
    }

    const productStr = selectedProducts.join(", ");
    if (
      productStr &&
      !selectedProducts.some((pr) => queryLower.includes(pr.toLowerCase()))
    ) {
      finalQuery = finalQuery + " for " + productStr;
    }

    if (
      selectedRegion &&
      !regionList.some((r: any) => queryLower.includes(String(r).toLowerCase()))
    ) {
      finalQuery = finalQuery + ` in ${selectedRegion} region`;
    }

    if (
      selectedSlot &&
      selectedSlot !== "all" &&
      !queryLower.includes(selectedSlot.toLowerCase())
    ) {
      const slotLabel =
        slotOptions.find((o) => o.value === selectedSlot)?.label ||
        selectedSlot;
      finalQuery = finalQuery + ` during ${slotLabel}`;
    }

    if (
      selectedVolumeCategory &&
      !queryLower.includes(selectedVolumeCategory.toLowerCase())
    ) {
      if (selectedVolumeCategory === "Both") {
        finalQuery = finalQuery + " for both Drawal and Injection";
      } else {
        finalQuery = finalQuery + " " + selectedVolumeCategory + " volume";
      }
    }

    if (selectedYear === "last3" && !/\b20\d{2}\b/.test(queryLower)) {
      finalQuery = finalQuery + " for the last 3 years";
    }

    if (meta.dimensions?.period_suggestion) {
      if (periodMode === "month") {
        finalQuery += ` in ${periodValue.month} ${periodValue.year}`;
      } else if (periodMode === "year") {
        finalQuery += ` in ${periodValue.year}`;
      } else if (periodMode === "range") {
        finalQuery += ` from ${periodValue.startDate} to ${periodValue.endDate}`;
      }
    }

    onSend?.(finalQuery, {
      isRefinement: true,
      originalMsgId: message.id,
      selections: {
        entity: selectedEntity,
        exchange: exchangeStr,
        product: productStr,
        region: selectedRegion,
        slot: selectedSlot,
        year: selectedYear,
        volume_category: selectedVolumeCategory,
      },
    });
  };

  const renderChart = () => {
    const hasDynamic = chart_data && meta?.chart_meta;
    const { x_candidates, y_candidates, units } = meta?.chart_meta || {};

    const defaults = meta?.chart_meta || {};
    const isCustomized =
      (activeX && defaults.default_x && activeX !== defaults.default_x) ||
      (activeY && defaults.default_y && activeY !== defaults.default_y);

    let plotComponent = null;
    let isDynamicRender = false;

    if (hasDynamic && (isCustomized || !plot_json)) {
      try {
        const rawData = JSON.parse(chart_data);
        const palette = [
          BRAND_COLOR,
          "#0ea5a3",
          "#22c55e",
          ACCENT_COLOR,
          "#38bdf8",
          "#fb7185",
        ];
        const curX = activeX || defaults.default_x || x_candidates?.[0];
        const curY = activeY || defaults.default_y || y_candidates?.[0];

        if (!rawData || rawData.length === 0)
          throw new Error("Empty chart data");

        const groupKey =
          meta.viz_type === "bar"
            ? rawData[0]?.entity_name
              ? "entity_name"
              : rawData[0]?.product_name
                ? "product_name"
                : rawData[0]?.name
                  ? "name"
                  : null
            : null;

        let plotData = [];
        if (groupKey && meta.viz_type === "bar") {
          const groups = [...new Set(rawData.map((r: any) => r[groupKey]))];
          plotData = groups.map((g, i) => ({
            name: String(g),
            x: rawData
              .filter((r: any) => r[groupKey] === g)
              .map((r: any) => r[curX]),
            y: rawData
              .filter((r: any) => r[groupKey] === g)
              .map((r: any) => r[curY]),
            type: meta.viz_type || "scatter",
            marker: { color: palette[i % palette.length] },
          }));
        } else {
          plotData = [
            {
              x: rawData.map((r: any) => r[curX]),
              y: rawData.map((r: any) => r[curY]),
              type: meta.viz_type || "scatter",
              mode: meta.viz_type === "bar" ? undefined : "lines+markers",
              marker: { color: BRAND_COLOR },
              line: { shape: "spline", smoothing: 1.3 },
            },
          ];
        }

        isDynamicRender = true;
        plotComponent = (
          <Plot
            data={plotData as any}
            layout={{
              autosize: true,
              margin: { l: 60, r: 20, t: 40, b: 60 },
              font: { family: UI_FONT, color: "#9aa6b2" },
              paper_bgcolor: "rgba(0,0,0,0)",
              plot_bgcolor: "rgba(0,0,0,0)",
              xaxis: {
                title: {
                  text: formatColumnName(String(curX || "")),
                },
                gridcolor: "rgba(255,255,255,0.05)",
                zeroline: false,
              },
              yaxis: {
                title: {
                  text: `${formatColumnName(String(curY || ""))} (${units?.[curY] || ""})`,
                },
                gridcolor: "rgba(255,255,255,0.05)",
                zeroline: false,
              },
              legend: { orientation: "h", y: -0.2 },
            }}
            useResizeHandler
            style={{ width: "100%", height: "100%", minHeight: "450px" }}
            config={{ responsive: true, displayModeBar: true }}
          />
        );
      } catch (e) {
        console.error("Dynamic plot error:", e);
      }
    }

    if (!plotComponent && plot_json) {
      try {
        const fig = JSON.parse(plot_json);
        plotComponent = (
          <Plot
            data={fig.data}
            layout={{
              ...fig.layout,
              autosize: true,
              margin: { l: 40, r: 20, t: 40, b: 40 },
              font: { family: UI_FONT },
              xaxis: { ...(fig.layout?.xaxis || {}) },
              yaxis: { ...(fig.layout?.yaxis || {}) },
            }}
            useResizeHandler={true}
            style={{ width: "100%", height: "100%", minHeight: "450px" }}
            config={{ responsive: true, displayModeBar: true }}
          />
        );
      } catch (e) {
        plotComponent = (
          <div className="error-card">Plot Error: {(e as Error).message}</div>
        );
      }
    }

    if (!plotComponent && plot_b64) {
      plotComponent = (
        <img
          src={
            plot_b64.startsWith("data:")
              ? plot_b64
              : `data:image/png;base64,${plot_b64}`
          }
          alt="Chart"
          style={{
            maxWidth: "100%",
            borderRadius: "8px",
            border: "1px solid var(--border)",
          }}
        />
      );
    }

    if (!plotComponent)
      return (
        <div style={{ padding: "20px", color: "white" }}>
          No Chart Data Available
        </div>
      );

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {hasDynamic && (
          <div
            style={{
              display: "flex",
              gap: "12px",
              padding: "12px",
              background: "rgba(255,255,255,0.03)",
              borderRadius: "12px",
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            <div style={{ flex: 1 }}>
              <span
                style={{
                  fontSize: "10px",
                  color: "white",
                  fontWeight: 600,
                  display: "block",
                  marginBottom: "4px",
                }}
              >
                X-AXIS
              </span>
              <select
                value={activeX || defaults.default_x}
                onChange={(e) => setActiveX(e.target.value)}
                style={{
                  width: "100%",
                  background: "rgba(12, 18, 22, 0.9)",
                  border: "1px solid var(--border)",
                  color: "var(--ink-900)",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  fontSize: "12px",
                }}
              >
                {x_candidates?.map((c: any) => (
                  <option key={c} value={c}>
                    {formatColumnName(String(c))}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <span
                style={{
                  fontSize: "10px",
                  color: "white",
                  fontWeight: 600,
                  display: "block",
                  marginBottom: "4px",
                }}
              >
                Y-AXIS
              </span>
              <select
                value={activeY || defaults.default_y}
                onChange={(e) => setActiveY(e.target.value)}
                style={{
                  width: "100%",
                  background: "rgba(12, 18, 22, 0.9)",
                  border: "1px solid var(--border)",
                  color: "var(--ink-900)",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  fontSize: "12px",
                }}
              >
                {y_candidates?.map((c: any) => (
                  <option key={c} value={c}>
                    {formatColumnName(String(c))}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                className="chip-btn"
                style={{ padding: "6px 10px", fontSize: "11px" }}
                onClick={() => {
                  setActiveX(defaults.default_x || "");
                  setActiveY(defaults.default_y || "");
                }}
              >
                Reset
              </button>
            </div>
          </div>
        )}
        <div
          style={{
            background: isDynamicRender ? "rgba(0,0,0,0.2)" : "white",
            borderRadius: "12px",
            overflow: "auto",
            padding: "8px",
            minHeight: "450px",
            border: isDynamicRender
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid #e2e8f0",
          }}
        >
          {plotComponent}
        </div>
      </div>
    );
  };

  const hasSuggestionContent = Boolean(
    meta?.interactive_suggestions ||
    meta?.dimensions?.exchanges ||
    meta?.dimensions?.products ||
    meta?.dimensions?.volume_categories ||
    meta?.dimensions?.period_suggestion ||
    meta?.dimensions?.slot_presets,
  );

  const summaryText =
    typeof meta?.summary === "string" ? meta.summary : message.content;
  const normalizedSummary =
    typeof summaryText === "string"
      ? summaryText.replace(/\\n/g, "\n")
      : summaryText;

  return (
    <div style={{ marginTop: "10px" }}>
      <TabSwitcher
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <div style={{ animation: "fadeIn 0.3s ease" }}>
        {activeTab === "Chart" && renderChart()}

        {activeTab === "Summary" && (
          <div
            className="prose"
            style={{ fontSize: "15px", lineHeight: "1.6" }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={markdownComponents}
            >
              {normalizedSummary}
            </ReactMarkdown>
          </div>
        )}

        {activeTab === "Table" && showTable && (
          <div>
            <SimpleTableFromCsv
              csv={csv as string}
              onDownload={
                onDownloadCsv ? () => onDownloadCsv(message) : undefined
              }
            />
          </div>
        )}
      </div>

      {/* Interactive Selection Block */}
      {hasSuggestionContent && (
        <div style={{ marginTop: "24px" }}>
          {meta.applied ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "8px 16px",
                background:
                  "linear-gradient(140deg, rgba(49, 45, 40, 0.9), rgba(40, 38, 35, 0.9))",
                borderRadius: "8px",
                border: "1px solid gray",
                fontSize: "13px",
                color: "white",
                fontWeight: 500,
                animation: "fadeIn 0.5s ease",
              }}
            >
              <span style={{ fontSize: "14px" }}>★</span>
              <span>Applied: </span>
              <span style={{ color: "white" }}>{meta.applied.entity}</span>
              {meta.applied.exchange && <span style={{ opacity: 0.4 }}>•</span>}
              <span style={{ color: "white" }}>{meta.applied.exchange}</span>
              {meta.applied.product && <span style={{ opacity: 0.4 }}>•</span>}
              <span style={{ color: "white" }}>{meta.applied.product}</span>
            </div>
          ) : (
            <div
              style={{
                padding: "24px",
                background: "rgba(255,255,255,0.02)",
                borderRadius: "16px",
                border: "1px solid var(--border)",
                boxShadow: "inset 0 0 20px rgba(0,0,0,0.2)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  marginBottom: "8px",
                }}
              >
                {meta.interactive_suggestions && (
                  <div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "white",
                        textTransform: "uppercase",
                        marginBottom: "12px",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                      }}
                    >
                      Entity
                    </div>
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                    >
                      {meta.interactive_suggestions.map((s: string) => (
                        <button
                          key={s}
                          className={`chip-btn ${selectedEntity === s ? "active" : ""}`}
                          onClick={() => setSelectedEntity(s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: "48px", flexWrap: "wrap" }}>
                  {meta.dimensions?.exchanges && (
                    <div style={{ flex: 1, minWidth: "200px" }}>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "white",
                          textTransform: "uppercase",
                          marginBottom: "12px",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                        }}
                      >
                        Exchange
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        {meta.dimensions.exchanges.map((s: string) => (
                          <button
                            key={s}
                            className={`chip-btn ${selectedExchanges.includes(s) ? "active" : ""}`}
                            onClick={() =>
                              toggleSelection(
                                selectedExchanges,
                                setSelectedExchanges,
                                s,
                              )
                            }
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {meta.dimensions?.products && (
                    <div style={{ flex: 1, minWidth: "200px" }}>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "white",
                          textTransform: "uppercase",
                          marginBottom: "12px",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                        }}
                      >
                        Product
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        {meta.dimensions.products.map((s: string) => (
                          <button
                            key={s}
                            className={`chip-btn ${selectedProducts.includes(s) ? "active" : ""}`}
                            onClick={() =>
                              toggleSelection(
                                selectedProducts,
                                setSelectedProducts,
                                s,
                              )
                            }
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {meta.dimensions?.volume_categories && (
                    <div style={{ flex: 1, minWidth: "200px" }}>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "white",
                          textTransform: "uppercase",
                          marginBottom: "12px",
                          fontWeight: 600,
                          letterSpacing: "0.05em",
                        }}
                      >
                        Volume Category
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        {meta.dimensions.volume_categories.map((s: string) => (
                          <button
                            key={s}
                            className={`chip-btn ${selectedVolumeCategory === s ? "active" : ""}`}
                            onClick={() => setSelectedVolumeCategory(s)}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {meta.dimensions?.slot_presets && (
                  <div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "white",
                        textTransform: "uppercase",
                        marginBottom: "12px",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                      }}
                    >
                      Slot Window
                    </div>
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                    >
                      {meta.dimensions.slot_presets.map((s: string) => (
                        <button
                          key={s}
                          className={`chip-btn ${selectedSlot === s.toLowerCase() ? "active" : ""}`}
                          onClick={() => setSelectedSlot(s.toLowerCase())}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {meta.dimensions?.period_suggestion && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "white",
                        textTransform: "uppercase",
                        marginBottom: "12px",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>Time Period</span>
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          background: "rgba(255,255,255,0.05)",
                          padding: "2px",
                          borderRadius: "6px",
                        }}
                      >
                        {["month", "year", "range"].map((m) => (
                          <button
                            key={m}
                            onClick={() =>
                              setPeriodMode(m as "month" | "year" | "range")
                            }
                            style={{
                              background:
                                periodMode === m
                                  ? "var(--brand-500)"
                                  : "transparent",
                              color: periodMode === m ? "white" : "white",
                              border: "none",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              fontSize: "10px",
                              textTransform: "uppercase",
                              cursor: "pointer",
                            }}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        padding: "20px",
                        borderRadius: "12px",
                        border: "1px solid rgba(255,255,255,0.05)",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                      }}
                    >
                      {periodMode === "month" && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              overflowX: "auto",
                              paddingBottom: "8px",
                              borderBottom: "1px solid rgba(255,255,255,0.05)",
                            }}
                          >
                            {[2023, 2024, 2025, 2026].map((y) => (
                              <button
                                key={y}
                                onClick={() =>
                                  setPeriodValue({
                                    ...periodValue,
                                    year: String(y),
                                  })
                                }
                                style={{
                                  padding: "6px 16px",
                                  borderRadius: "20px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  border: "1px solid transparent",
                                  background:
                                    String(periodValue.year) === String(y)
                                      ? "var(--brand-500)"
                                      : "rgba(255,255,255,0.05)",
                                  color: "white",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {y}
                              </button>
                            ))}
                          </div>

                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(4, 1fr)",
                              gap: "10px",
                            }}
                          >
                            {[
                              "Jan",
                              "Feb",
                              "Mar",
                              "Apr",
                              "May",
                              "Jun",
                              "Jul",
                              "Aug",
                              "Sep",
                              "Oct",
                              "Nov",
                              "Dec",
                            ].map((m) => (
                              <button
                                key={m}
                                onClick={() =>
                                  setPeriodValue({ ...periodValue, month: m })
                                }
                                style={{
                                  padding: "12px 8px",
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  border: "1px solid transparent",
                                  background:
                                    periodValue.month === m
                                      ? "rgba(99, 102, 241, 0.2)"
                                      : "transparent",
                                  color:
                                    periodValue.month === m
                                      ? "var(--brand-400)"
                                      : "white",
                                  boxShadow:
                                    periodValue.month === m
                                      ? "inset 0 0 0 1px var(--brand-500)"
                                      : "none",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  textAlign: "center",
                                }}
                              >
                                {m}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {periodMode === "year" && (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fill, minmax(80px, 1fr))",
                            gap: "12px",
                          }}
                        >
                          {[2023, 2024, 2025, 2026].map((y) => (
                            <button
                              key={y}
                              onClick={() =>
                                setPeriodValue({
                                  ...periodValue,
                                  year: String(y),
                                })
                              }
                              style={{
                                padding: "16px 8px",
                                borderRadius: "12px",
                                fontSize: "14px",
                                fontWeight: 600,
                                border: "1px solid rgba(255,255,255,0.1)",
                                background:
                                  String(periodValue.year) === String(y)
                                    ? "linear-gradient(135deg, var(--brand-600), var(--brand-400))"
                                    : "rgba(255,255,255,0.02)",
                                color: "white",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                textAlign: "center",
                              }}
                            >
                              {y}
                            </button>
                          ))}
                        </div>
                      )}

                      {periodMode === "range" && (
                        <div
                          style={{
                            display: "flex",
                            gap: "20px",
                            alignItems: "center",
                            justifyContent: "center",
                            flexWrap: "wrap",
                            padding: "10px 0",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: "160px" }}>
                            <label
                              style={{
                                fontSize: "10px",
                                color: "white",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                marginBottom: "8px",
                                display: "block",
                              }}
                            >
                              From
                            </label>
                            <input
                              type="date"
                              value={periodValue.startDate}
                              onChange={(e) =>
                                setPeriodValue({
                                  ...periodValue,
                                  startDate: e.target.value,
                                })
                              }
                              style={{
                                width: "100%",
                                background: "rgba(15, 23, 42, 0.6)",
                                border: "1px solid var(--border)",
                                color: "white",
                                padding: "12px",
                                borderRadius: "8px",
                                colorScheme: "dark",
                                fontSize: "14px",
                                outline: "none",
                              }}
                            />
                          </div>
                          <div
                            style={{
                              color: "#475569",
                              fontSize: "20px",
                              fontWeight: 300,
                              paddingTop: "20px",
                            }}
                          >
                            ⟶
                          </div>
                          <div style={{ flex: 1, minWidth: "160px" }}>
                            <label
                              style={{
                                fontSize: "10px",
                                color: "white",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                marginBottom: "8px",
                                display: "block",
                              }}
                            >
                              To
                            </label>
                            <input
                              type="date"
                              value={periodValue.endDate}
                              onChange={(e) =>
                                setPeriodValue({
                                  ...periodValue,
                                  endDate: e.target.value,
                                })
                              }
                              style={{
                                width: "100%",
                                background: "rgba(15, 23, 42, 0.6)",
                                border: "1px solid var(--border)",
                                color: "white",
                                padding: "12px",
                                borderRadius: "8px",
                                colorScheme: "dark",
                                fontSize: "14px",
                                outline: "none",
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div
                style={{
                  borderTop: "1px solid var(--border)",
                  marginTop: "20px",
                  paddingTop: "16px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <button className="apply-btn" onClick={handleApply}>
                  Apply Selections & Execute
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {(footerRuntime || onCopy || onFeedback || onSend) && (
        <div className="message-footer">
          <div className="metadata-footer">
            {footerRuntime ? `Execution: ${footerRuntime}` : ""}
          </div>
          <div className="action-row">
            <button
              className="icon-btn"
              title="Copy"
              aria-label="Copy"
              onClick={() => onCopy?.(meta?.summary || message.content)}
            >
              📋
            </button>
            <button
              className={`icon-btn ${feedbackValue === "up" ? "active" : ""}`}
              title="Thumbs up"
              aria-label="Thumbs up"
              onClick={() => onFeedback?.("up", message)}
            >
              👍
            </button>
            <button
              className={`icon-btn ${feedbackValue === "down" ? "active" : ""}`}
              title="Thumbs down"
              aria-label="Thumbs down"
              onClick={() => onFeedback?.("down", message)}
            >
              👎
            </button>
            <button
              className="icon-btn"
              title="Regenerate"
              aria-label="Regenerate"
              onClick={() =>
                onSend?.(
                  message.question || meta?.initial_query || message.content,
                )
              }
            >
              ⟳
            </button>
            <button
              className="icon-btn"
              title="Refresh"
              aria-label="Refresh"
              onClick={() => window.location.reload()}
            >
              ↻
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* --- COMPONENT: MESSAGE CARD --- */

interface MessageProps {
  message: MessageData;
  onCopy: (text: string) => void;
  onEdit: (text: string) => void;
  onFeedback: (type: "up" | "down", msg: MessageData) => void;
  onDownloadCsv: (msg: MessageData) => void;
  onDownloadPlot?: (msg: MessageData) => void;
  onSend: (text: string, options?: any) => void;
  feedback: Record<string, "up" | "down">;
  metaOptions: any;
}

const Message: React.FC<MessageProps> = ({
  message,
  onCopy,
  onEdit,
  onFeedback,
  onDownloadCsv,
  onDownloadPlot,
  onSend,
  feedback,
  metaOptions,
}) => {
  const isUser = message.role === "user";

  // Don't render streaming messages without content
  if (
    !isUser &&
    message.isStreaming &&
    !message.content &&
    !message.meta?.summary
  ) {
    return null;
  }

  // New "ResultRenderer" Logic
  if (!isUser && message.meta) {
    return (
      <div className={`message-row message-assistant`}>
        <div
          className={message.isError ? THEME.errorCard : THEME.assistantCard}
        >
          <div className="card-padding">
            <ResultRenderer
              message={message}
              onDownloadCsv={onDownloadCsv}
              onSend={onSend}
              onCopy={onCopy}
              onFeedback={onFeedback}
              feedback={feedback}
              metaOptions={metaOptions}
            />
          </div>
        </div>
      </div>
    );
  }

  // Legacy Logic (Fallback)
  const TableRenderer = ({ children }: any) => {
    return (
      <div className="data-table-card">
        <div className="table-header-context">
          <span>Data View</span>
          {message.csv && (
            <button
              className="download-csv-btn"
              onClick={() => onDownloadCsv(message)}
            >
              <span style={{ fontSize: "14px" }}>⬇</span> Download CSV
            </button>
          )}
        </div>
        <div className="table-scroller">
          <table className="dense-table">{children}</table>
        </div>
      </div>
    );
  };

  const PlotContainer = ({ jsonStr }: { jsonStr: string }) => {
    try {
      const fig = JSON.parse(jsonStr);
      return (
        <div
          style={{
            marginTop: "16px",
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid var(--border)",
            minHeight: "450px",
            background: "var(--surface)",
          }}
        >
          <Plot
            data={fig.data}
            layout={{
              ...fig.layout,
              autosize: true,
              margin: { l: 40, r: 20, t: 40, b: 40 },
              font: { family: UI_FONT },
            }}
            useResizeHandler={true}
            style={{ width: "100%", height: "100%", minHeight: "450px" }}
            config={{ responsive: true, displayModeBar: true }}
          />
        </div>
      );
    } catch (e) {
      return (
        <div className="error-card">
          Failed to render plot: {(e as Error).message}
        </div>
      );
    }
  };

  const markdownComponentsWithTable = {
    ...markdownComponents,
    table: TableRenderer,
  };

  return (
    <div
      className={`message-row ${isUser ? "message-user" : "message-assistant"}`}
    >
      {isUser ? (
        <div className={THEME.userBubble}>
          {message.content}
          <div
            style={{
              position: "absolute",
              bottom: "-24px",
              right: "0",
              display: "flex",
              gap: "8px",
            }}
          >
            <button
              onClick={() => onCopy(message.content)}
              className="icon-btn"
              title="Copy"
              aria-label="Copy"
            >
              📋
            </button>
            <button
              onClick={() => onEdit(message.content)}
              className="icon-btn"
              title="Edit"
              aria-label="Edit"
            >
              ✏️
            </button>
          </div>
        </div>
      ) : (
        <div
          className={message.isError ? THEME.errorCard : THEME.assistantCard}
          style={
            message.isError
              ? { borderColor: "#ef4444", background: "rgba(239, 68, 68, 0.1)" }
              : {}
          }
        >
          {!message.meta?.applied && (
            <div className="card-padding prose">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={markdownComponentsWithTable}
              >
                {message.content}
              </ReactMarkdown>
              {message.plot_json ? (
                <PlotContainer jsonStr={message.plot_json} />
              ) : (
                message.plot_b64 && (
                  <div
                    style={{
                      marginTop: "16px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid var(--border)",
                    }}
                  >
                    <img
                      src={`data:image/png;base64,${message.plot_b64}`}
                      alt="Analysis Chart"
                      style={{ width: "100%", display: "block" }}
                    />
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* --- MAIN APP --- */
interface Session {
  id: string;
  title: string;
  ts: number;
}

interface PollingRef {
  active: boolean;
  timeoutId: NodeJS.Timeout | null;
  taskId: string | null;
  assistantId: string | null;
}

function App() {
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(
    () => localStorage.getItem("vidyut_session_id") || null,
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const pollingRef = useRef<PollingRef>({
    active: false,
    timeoutId: null,
    taskId: null,
    assistantId: null,
  });
  const historyAbortRef = useRef<AbortController | null>(null);

  const STREAMING_ENABLED = true;
  const ASYNC_POLLING_ENABLED = false;

  const [feedback, setFeedback] = useState<Record<string, "up" | "down">>({});
  const [quickPrompts, setQuickPrompts] = useState<string[]>(fallbackPrompts);

  const [allSuggestions, setAllSuggestions] = useState<string[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [metaOptions, setMetaOptions] = useState<any>({
    regions: [],
    exchanges: [],
    products: [],
    trade_types: [],
    timeslots: [],
  });

  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [deletePhaseId, setDeletePhaseId] = useState<string | null>(null);
  const [sessionCache, setSessionCache] = useState<
    Record<string, MessageData[]>
  >({});

  const apiBase = useMemo(() => BASE_URL.replace(/\/$/, ""), []);

  useEffect(() => {
    fetchSessions();
    fetchSuggestions();
    fetchMetadata();
    if (sessionId) {
      loadSession(sessionId, { force: true, silent: true });
    }
  }, []);

  const fetchSuggestions = async () => {
    try {
      const res = await fetch(`${apiBase}/exchange_assistent/api/suggestions`);
      if (res.ok) {
        const data = await res.json();
        setAllSuggestions(data.suggestions || []);
      }
    } catch {}
  };

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${apiBase}/exchange_assistent/api/sessions`);
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch {}
  };

  const fetchMetadata = async () => {
    try {
      const res = await fetch(`${apiBase}/exchange_assistent/api/metadata`);
      if (res.ok) {
        const data = await res.json();
        setMetaOptions({
          regions: Array.isArray(data.regions)
            ? data.regions.filter(Boolean)
            : [],
          exchanges: Array.isArray(data.exchanges)
            ? data.exchanges.filter(Boolean)
            : [],
          products: Array.isArray(data.products)
            ? data.products.filter(Boolean)
            : [],
          trade_types: Array.isArray(data.trade_types)
            ? data.trade_types.filter(Boolean)
            : [],
          timeslots: Array.isArray(data.timeslots)
            ? data.timeslots.filter(Boolean)
            : [],
        });
      }
    } catch {
      setMetaOptions({
        regions: [],
        exchanges: [],
        products: [],
        trade_types: [],
        timeslots: [],
      });
    }
  };

  const loadSession = async (
    sid: string,
    options: { force?: boolean; silent?: boolean } = {},
  ) => {
    const { force = false, silent = false } = options;
    if (!force && sid === sessionId) return;

    if (sessionCache[sid]) {
      setMessages(sessionCache[sid]);
    } else if (!silent) {
      setMessages([]);
    }

    try {
      if (historyAbortRef.current) historyAbortRef.current.abort();
      historyAbortRef.current = new AbortController();

      const res = await fetch(
        `${apiBase}/exchange_assistent/api/chat/${sid}?limit=200&lite=1`,
        {
          signal: historyAbortRef.current.signal,
          headers: {
            Authorization: `${getAccessToken() || ""}`,
          },
        },
      );

      if (res.ok) {
        const data = await res.json();
        const history = data.history || [];
        setMessages(history);
        setSessionCache((prev) => ({ ...prev, [sid]: history }));
        setSessionId(sid);
        localStorage.setItem("vidyut_session_id", sid);
      }
    } catch (e) {
      console.error("Failed to load session", e);
    }
  };

  const handleFeedback = async (type: "up" | "down", msg: MessageData) => {
    setFeedback((prev) => ({ ...prev, [msg.id]: type }));
    try {
      await fetch(`${apiBase}/exchange_assistent/api/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${getAccessToken() || ""}`,
        },
        body: JSON.stringify({
          message_id: msg.id,
          feedback: type,
          sql: msg.sql,
          session_id: sessionId,
        }),
      });
    } catch (e) {
      console.error("Feedback failed", e);
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    localStorage.removeItem("vidyut_session_id");
    setMessages([]);
    inputRef.current?.focus();
  };

  const handleDeleteSession = async (sid: string) => {
    try {
      const res = await fetch(
        `${apiBase}/exchange_assistent/api/sessions/${sid}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `${getAccessToken() || ""}`,
          },
        },
      );
      if (res.ok) {
        setSessions((prev) => prev.filter((s) => s.id !== sid));
        if (sessionId === sid) {
          startNewChat();
        }
      }
    } catch (e) {
      console.error("Failed to delete session", e);
    }
  };

  const handleDownloadCsv = (msg: MessageData) => {
    if (!msg.csv) return;
    const blob = new Blob([msg.csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `analysis_data_${msg.ts}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEdit = (content: string) => {
    setInput(content);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const isLongQuery = (text: string) => {
    const t = (text || "").toLowerCase();
    if (t.length >= 80) return true;
    if (
      /(trend|pattern|compare|comparison|last\s+\d+|month|months|year|years|week|weeks|daily|hourly|slot|range|between)/.test(
        t,
      )
    ) {
      return true;
    }
    return false;
  };

  const sendMessage = async (text: string, options: any = {}) => {
    if (!text.trim() || isSending) return;
    const content = text.trim();
    const { isRefinement, originalMsgId, selections } = options;

    if (isRefinement) {
      setMessages((prev) => {
        const next = [...prev];
        const asstIdx = next.findIndex((m) => m.id === originalMsgId);
        if (asstIdx > 0) {
          const userIdx = asstIdx - 1;
          if (next[userIdx].role === "user") {
            next[userIdx] = { ...next[userIdx], content };
          }
          next[asstIdx] = {
            ...next[asstIdx],
            meta: { ...next[asstIdx].meta, applied: selections },
          };
        }
        return next;
      });
    } else {
      const userMsg: MessageData = {
        id: `user-${Date.now()}`,
        role: "user",
        content,
        ts: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);
    }

    setInput("");
    setIsSending(true);

    abortControllerRef.current = new AbortController();
    const requestId = `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    requestIdRef.current = requestId;

    setTimeout(
      () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
      50,
    );

    const updateAssistant = (id: string, updater: any) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== id) return m;
          if (typeof updater === "function") return { ...m, ...updater(m) };
          return { ...m, ...updater };
        }),
      );
    };

    let streamingId: string | null = null;
    try {
      const useAsyncPolling = ASYNC_POLLING_ENABLED && isLongQuery(content);

      if (useAsyncPolling) {
        streamingId = `asst-${Date.now()}`;
        setMessages((prev) => [
          ...prev,
          {
            id: streamingId!,
            role: "assistant",
            content: "",
            ts: Date.now(),
            isError: false,
            isStreaming: true,
          },
        ]);

        const res = await fetch(`${apiBase}/exchange_assistent/api/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${getAccessToken() || ""}`,
          },
          body: JSON.stringify({
            message: content,
            history: messages,
            session_id: sessionId,
            request_id: requestId,
            replace_last_user: Boolean(isRefinement),
            async: true,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        const taskId = data.task_id;
        if (!taskId) throw new Error("Missing task id");

        pollingRef.current.active = true;
        pollingRef.current.taskId = taskId;
        pollingRef.current.assistantId = streamingId;

        const poll = async () => {
          if (!pollingRef.current.active) return;
          try {
            const statusRes = await fetch(
              `${apiBase}/exchange_assistent/api/chat/status/${taskId}`,
              {
                headers: {
                  Authorization: `${getAccessToken() || ""}`,
                },
              },
            );
            if (!statusRes.ok) throw new Error("Polling failed");
            const statusData = await statusRes.json();

            if (statusData.status === "ready") {
              const payload = statusData;
              if (payload.session_id && payload.session_id !== sessionId) {
                setSessionId(payload.session_id);
                localStorage.setItem("vidyut_session_id", payload.session_id);
                fetchSessions();
              }
              updateAssistant(streamingId!, {
                content: payload.reply || "",
                sql: payload.sql,
                question: payload.question,
                csv: payload.csv,
                plot_b64: payload.plot_b64,
                plot_json: payload.plot_json,
                chart_data: payload.chart_data,
                meta: payload.meta,
                isStreaming: false,
              });
              pollingRef.current.active = false;
              return;
            }

            if (
              statusData.status === "cancelled" ||
              statusData.status === "failed"
            ) {
              updateAssistant(streamingId!, {
                content:
                  statusData.status === "cancelled"
                    ? "**Request cancelled.**"
                    : "**Error:** Async task failed.",
                isError: statusData.status === "failed",
                isStreaming: false,
              });
              pollingRef.current.active = false;
              return;
            }
          } catch (err) {
            console.error(err);
          }
          pollingRef.current.timeoutId = setTimeout(poll, 1200);
        };

        poll();
      } else if (STREAMING_ENABLED) {
        streamingId = `asst-${Date.now()}`;
        setMessages((prev) => [
          ...prev,
          {
            id: streamingId!,
            role: "assistant",
            content: "",
            ts: Date.now(),
            isError: false,
            isStreaming: true,
          },
        ]);

        const res = await fetch(`${apiBase}/exchange_assistent/api/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${getAccessToken() || ""}`,
          },
          body: JSON.stringify({
            message: content,
            history: messages,
            session_id: sessionId,
            request_id: requestId,
            replace_last_user: Boolean(isRefinement),
            stream: true,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!res.ok || !res.body) throw new Error("Failed");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let done = false;

        const handleEvent = (eventName: string, dataStr: string) => {
          if (!dataStr) return;
          let payload = null;
          try {
            payload = JSON.parse(dataStr);
          } catch {
            payload = null;
          }

          if (eventName === "chunk" && payload?.text) {
            if (payload.text.startsWith("__STAGE__")) {
              const stageMsg = payload.text.replace("__STAGE__", "");
              updateAssistant(streamingId!, { content: `• ${stageMsg}` });
            } else {
              updateAssistant(streamingId!, (prev: MessageData) => ({
                content: (prev.content || "") + payload.text,
              }));
            }
          } else if (eventName === "stage" && payload?.message) {
            updateAssistant(streamingId!, { content: `• ${payload.message}` });
          } else if (eventName === "done" && payload) {
            if (payload.session_id && payload.session_id !== sessionId) {
              setSessionId(payload.session_id);
              localStorage.setItem("vidyut_session_id", payload.session_id);
              fetchSessions();
            }
            updateAssistant(streamingId!, {
              content: payload.reply || "",
              sql: payload.sql,
              question: payload.question,
              csv: payload.csv,
              plot_b64: payload.plot_b64,
              plot_json: payload.plot_json,
              chart_data: payload.chart_data,
              meta: payload.meta,
              isStreaming: false,
            });
            done = true;
          } else if (eventName === "error") {
            updateAssistant(streamingId!, {
              content: `**Error:** ${payload?.message || "Failed to connect to backend."}`,
              isError: true,
              isStreaming: false,
            });
            done = true;
          }
        };

        while (!done) {
          const { value, done: doneReading } = await reader.read();
          if (doneReading) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";

          for (const part of parts) {
            const lines = part.split("\n");
            let eventName = "message";
            let dataLines: string[] = [];

            for (const line of lines) {
              if (line.startsWith("event:")) {
                eventName = line.replace("event:", "").trim();
              } else if (line.startsWith("data:")) {
                dataLines.push(line.replace("data:", "").trim());
              }
            }
            handleEvent(eventName, dataLines.join("\n"));
            if (done) break;
          }
        }
      } else {
        const res = await fetch(`${apiBase}/exchange_assistent/api/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${getAccessToken() || ""}`,
          },
          body: JSON.stringify({
            message: content,
            history: messages,
            session_id: sessionId,
            request_id: requestId,
            replace_last_user: Boolean(isRefinement),
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!res.ok) throw new Error("Failed");
        const data = await res.json();

        if (data.session_id && data.session_id !== sessionId) {
          setSessionId(data.session_id);
          localStorage.setItem("vidyut_session_id", data.session_id);
          fetchSessions();
        }

        const assistantMsg: MessageData = {
          id: `asst-${Date.now()}`,
          role: "assistant",
          content: data.reply || "No content",
          sql: data.sql,
          question: data.question,
          csv: data.csv,
          plot_b64: data.plot_b64,
          plot_json: data.plot_json,
          chart_data: data.chart_data,
          meta: data.meta,
          ts: Date.now(),
          isError: false,
        };
        setMessages((prev) => [...prev, assistantMsg]);
      }
    } catch (e: any) {
      if (e.name === "AbortError") {
        if (streamingId) {
          updateAssistant(streamingId, {
            content: "**Request cancelled.**",
            isStreaming: false,
          });
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "**Request cancelled.**",
              id: `cancel-${Date.now()}`,
              ts: Date.now(),
              isError: false,
            },
          ]);
        }
      } else {
        console.error(e);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `**Error:** ${e.message || "Failed to connect to backend."}`,
            id: `err-${Date.now()}`,
            ts: Date.now(),
            isError: true,
          },
        ]);
      }
    } finally {
      if (pollingRef.current.active) {
        // Keep polling running
      } else {
        pollingRef.current.timeoutId = null;
        pollingRef.current.taskId = null;
        pollingRef.current.assistantId = null;
      }
      abortControllerRef.current = null;
      requestIdRef.current = null;
      setIsSending(false);
      setTimeout(
        () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
        50,
      );
    }
  };

  const handleStop = () => {
    const reqId = requestIdRef.current;
    if (reqId) {
      fetch(`${apiBase}/exchange_assistent/api/cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${getAccessToken() || ""}`,
        },
        body: JSON.stringify({ session_id: sessionId, request_id: reqId }),
      }).catch(() => {});
    }

    if (pollingRef.current.active) {
      pollingRef.current.active = false;
      if (pollingRef.current.timeoutId) {
        clearTimeout(pollingRef.current.timeoutId);
      }
      const aid = pollingRef.current.assistantId;
      if (aid) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === aid
              ? { ...m, content: "**Request cancelled.**", isStreaming: false }
              : m,
          ),
        );
      }
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const groupedSessions = useMemo(() => {
    return sessions.reduce<Record<string, Session[]>>((groups, sess) => {
      const d = new Date(sess.ts);
      const now = new Date();
      const diff = Math.floor(
        (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24),
      );
      let label = "Older";
      if (diff === 0) label = "Today";
      else if (diff === 1) label = "Yesterday";
      else if (diff < 7) label = "Previous 7 Days";
      if (!groups[label]) groups[label] = [];
      groups[label].push(sess);
      return groups;
    }, {});
  }, [sessions]);

  return (
    <ErrorBoundary>
      <div className="app-shell">
        <div
          className={`${sidebarOpen ? "vidut-sidebar" : ""}`}
          onClick={() => setSidebarOpen(false)}
        >
          <aside className={`left_sidebar ${sidebarOpen ? "open" : ""}`}>
            <div className="sidebar-header">
              <div className="sidebar-brand">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    {/* <div className="brand-mark">V</div> */}
                    <div className="brand-text">
                      <div className="brand-title">Vidyut AI</div>
                      <div className="brand-subtitle">
                        Power Market Assistant
                      </div>
                    </div>
                  </div>
                  {/* {sidebarOpen && (
                  <button className="" onClick={() => setSidebarOpen(false)}>
                    X
                  </button>
                )} */}
                </div>
              </div>
              <button className="new-chat-btn mt-4" onClick={startNewChat}>
                <span>+</span> New Chat
              </button>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {["Today", "Yesterday", "Previous 7 Days", "Older"].map(
                (label) =>
                  groupedSessions[label] && (
                    <div key={label} className="session-group">
                      <div className="session-group-title">{label}</div>
                      {groupedSessions[label].map((s) => (
                        <div key={s.id}>
                          {deletePhaseId === s.id ? (
                            <div
                              style={{
                                background: "#fff",
                                border: "1px solid #fca5a5",
                                padding: "8px",
                                cursor: "default",
                                borderRadius: "6px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#991b1b",
                                  marginBottom: "8px",
                                  fontWeight: 600,
                                }}
                              >
                                Delete chat?
                              </div>
                              <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSession(s.id);
                                    setMenuOpenId(null);
                                    setDeletePhaseId(null);
                                  }}
                                  className="btn-danger"
                                >
                                  Delete
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeletePhaseId(null);
                                  }}
                                  className="btn-ghost"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`session-item gap-2 ${s.id === sessionId ? "active" : ""}`}
                              onClick={() => loadSession(s.id)}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                paddingRight: "10px",
                              }}
                            >
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                }}
                              >
                                {s.title}
                              </span>

                              <div style={{ position: "relative" }}>
                                <div
                                  className="session-actions"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeletePhaseId(s.id);
                                  }}
                                  style={{
                                    padding: "4px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Trash2 size={16} color="#ef4444" />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ),
              )}
            </div>
          </aside>
        </div>

        <main className="main-content">
          <div className="mobile-header">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                color: "var(--ink-700)",
              }}
            >
              ☰
            </button>
            <span style={{ fontWeight: 600, letterSpacing: "0.02em" }}>
              Vidyut AI
            </span>
          </div>

          <div className="feed-container ">
            {messages.length === 0 ? (
              <div className="hero">
                <h1 className="hero-title">Power Exchange Analytics</h1>
                <p className="hero-subtitle">
                  Ask complex queries about DAM, RTM, and Grid Data with
                  precision.
                </p>
                <div className="hero-grid">
                  {quickPrompts.map((p, i) => (
                    <button
                      key={i}
                      className="chip-btn"
                      onClick={() => sendMessage(p)}
                    >
                      <span style={{ opacity: 0.7 }}>➜</span> {p}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m) => (
                <Message
                  key={m.id}
                  message={m}
                  onCopy={(text) => text && navigator.clipboard.writeText(text)}
                  onEdit={handleEdit}
                  onFeedback={handleFeedback}
                  onDownloadCsv={handleDownloadCsv}
                  onSend={sendMessage}
                  metaOptions={metaOptions}
                  feedback={feedback}
                />
              ))
            )}

            {isSending && !messages.some((m) => m.isStreaming) && (
              <div className="message-row message-assistant">
                <div
                  className="assistant-card"
                  style={{
                    padding: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <img
                      src={Loader}
                      alt="Loader"
                      style={{ width: "20px", height: "20px" }}
                    />
                    <span style={{ color: "white", fontSize: "14px" }}>
                      Thinking...
                    </span>
                    <Loader />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} style={{ height: "20px" }}></div>
          </div>

          <div className="input-area">
            <div className="input-capsule">
              {showSuggestions && (
                <div className="suggestions-popover">
                  {filteredSuggestions.map((s, i) => (
                    <div
                      key={i}
                      className="suggestion-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setInput(s);
                        setShowSuggestions(false);
                        inputRef.current?.focus();
                      }}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
              <textarea
                ref={inputRef}
                className="chat-input"
                placeholder="Ask anything..."
                rows={1}
                value={input}
                onChange={(e) => {
                  const val = e.target.value;
                  setInput(val);
                  if (val.trim().length > 1) {
                    const matches = allSuggestions
                      .filter((s) =>
                        s.toLowerCase().includes(val.toLowerCase()),
                      )
                      .slice(0, 5);
                    setFilteredSuggestions(matches);
                    setShowSuggestions(matches.length > 0);
                  } else {
                    setShowSuggestions(false);
                  }
                }}
                onFocus={() =>
                  input.trim().length > 1 &&
                  filteredSuggestions.length > 0 &&
                  setShowSuggestions(true)
                }
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
              />
              {isSending ? (
                <button
                  className="send-btn-round stop-active"
                  onClick={handleStop}
                  title="Stop generating"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  className="send-btn-round"
                  disabled={!input.trim()}
                  onClick={() => sendMessage(input)}
                >
                  <SendHorizontal />
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </ErrorBoundary>
  );
}

export default App;
